import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/app/StatCard";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#10B981", "#F59E0B", "#EF4444", "#3B82F6", "#A1A1AA"];
const CHART_TICK_SM = { fontSize: 11 };
const CHART_BAR_RADIUS = [4, 4, 0, 0];

export default function Reports() {
  const { selectedId } = useProperty();
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [byZone, setByZone] = useState([]);
  const [byStaff, setByStaff] = useState([]);
  const [byProperty, setByProperty] = useState([]);

  useEffect(() => {
    (async () => {
      const params = user?.role === "super_admin" ? "" : `?property_id=${selectedId || ""}`;
      const [s, bs] = await Promise.all([
        api.get(`/reports/summary${params}`),
        api.get(`/reports/by-staff${params}`),
      ]);
      setSummary(s.data); setByStaff(bs.data);
      if (user?.role === "super_admin") {
        const bp = await api.get(`/reports/by-property`);
        setByProperty(bp.data);
      }
      if (selectedId) {
        const bz = await api.get(`/reports/by-zone?property_id=${selectedId}`);
        setByZone(bz.data);
      }
    })();
  }, [selectedId, user]);

  const taskBreakdown = summary ? [
    { name: "Completed", value: summary.completed_today, color: "#10B981" },
    { name: "Pending", value: summary.pending_today, color: "#F59E0B" },
    { name: "Overdue", value: summary.overdue, color: "#EF4444" },
    { name: "Issues", value: summary.issues, color: "#A1A1AA" },
  ].filter((x) => x.value > 0) : [];

  return (
    <div className="space-y-6">
      <div>
        <span className="label-overline">Analytics</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Reports</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Tasks Today" value={summary?.total_today ?? 0} testid="report-total" />
        <StatCard label="Completion" value={`${summary?.completion_pct ?? 0}%`} accent="green" testid="report-completion" />
        <StatCard label="Overdue" value={summary?.overdue ?? 0} accent="red" testid="report-overdue" />
        <StatCard label="Open Tickets" value={summary?.open_tickets ?? 0} accent="amber" testid="report-tickets" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 rounded-md p-6">
          <span className="label-overline">Today's Task Breakdown</span>
          <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-4">By status</h3>
          {taskBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={taskBreakdown} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50}>
                  {taskBreakdown.map((entry) => (<Cell key={entry.name} fill={entry.color} />))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-zinc-500 text-sm py-12 text-center">No data</div>}
        </div>

        {user?.role === "super_admin" && byProperty.length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-md p-6">
            <span className="label-overline">All Properties</span>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-4">Completion %</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={byProperty}>
                <XAxis dataKey="name" tick={CHART_TICK_SM} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="completion_pct" fill="#09090B" radius={CHART_BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {byZone.length > 0 && (
          <div className="bg-white border border-zinc-200 rounded-md p-6">
            <span className="label-overline">Zone-wise</span>
            <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-4">Completion by zone</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={byZone}>
                <XAxis dataKey="name" tick={CHART_TICK_SM} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="completion_pct" fill="#09090B" radius={CHART_BAR_RADIUS} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-6">
        <span className="label-overline">Staff Performance</span>
        <h3 className="font-display text-xl font-bold tracking-tight mt-1 mb-4">All staff</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 text-zinc-500 uppercase text-[10px] tracking-widest">
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Role</th>
                <th className="text-right py-2">Total</th>
                <th className="text-right py-2">Completed</th>
                <th className="text-right py-2">Issues</th>
                <th className="text-right py-2">%</th>
              </tr>
            </thead>
            <tbody>
              {byStaff.map((s) => (
                <tr key={s.user_id} className="border-b border-zinc-100">
                  <td className="py-2.5">{s.name}</td>
                  <td className="py-2.5 text-zinc-500 capitalize">{s.role.replace("_", " ")}</td>
                  <td className="py-2.5 text-right font-mono">{s.total}</td>
                  <td className="py-2.5 text-right font-mono">{s.completed}</td>
                  <td className="py-2.5 text-right font-mono text-red-600">{s.issues}</td>
                  <td className="py-2.5 text-right font-mono font-semibold">{s.completion_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
